import {ApiService} from "../api/ApiService";
import {useEffect, useState} from "react";
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    TextField
} from "@mui/material";
import {PartyType} from "../api/types/PatryType";
import {MatterType} from "../api/types/MatterType";
import {LoadingButton} from "@mui/lab";

interface CreateClientDialogProps {
    agentId: number;
    onCreate?: (data: CreateClientModel) => Promise<void>;
    children: (handleOpen: () => void, handleClose: () => void) => React.ReactNode;
}

interface CreateClientDialogState {
    name?: string;
    partyTypeId?: number;
    matterTypeId?: number;
}

export type CreateClientModel = Required<CreateClientDialogState>;

const CreateClientDialog = ({agentId, onCreate, children}: CreateClientDialogProps) => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const [newClient, setNewClient] = useState<CreateClientDialogState | null>(null);

    const [processing, setProcessing] = useState<boolean>(false);
    const [isValidate, setIsValidate] = useState<boolean>(false);

    const [partyTypes, setPartyTypes] = useState<PartyType[]>([]);
    const [loadingPartyTypes, setLoadingPartyTypes] = useState<boolean>(false);

    const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
    const [loadingMatterTypes, setLoadingMatterTypes] = useState<boolean>(false);

    const open = () => {
        setDialogOpen(true);
    }

    const close = () => {
        setNewClient(null);
        setIsValidate(false);
        setProcessing(false);
        setDialogOpen(false);
    }

    const handleCreateClick = async () => {
        if (!newClient?.name || !newClient?.partyTypeId || !newClient?.matterTypeId) {
            setIsValidate(true);
            return;
        }
        if (onCreate) {
            setProcessing(true);
            try {
                await onCreate({
                    name: newClient.name,
                    partyTypeId: newClient.partyTypeId,
                    matterTypeId: newClient.matterTypeId,
                });
            } catch {
            }
            setProcessing(false);
        }
        close();
    };

    const loadPartyTypes = async () => {
        setLoadingPartyTypes(true);
        const partyTypesResponse = await ApiService.GetAgentPartyTypes(agentId);
        if (partyTypesResponse.success) {
            setPartyTypes(partyTypesResponse.data);
        }
        setLoadingPartyTypes(false);
    }

    const loadMatterTypes = async () => {
        setLoadingMatterTypes(true);
        const matterTypesResponse = await ApiService.GetAgentMatterTypes(agentId);
        if (matterTypesResponse.success) {
            setMatterTypes(matterTypesResponse.data);
        }
        setLoadingMatterTypes(false);
    }

    useEffect(() => {
        if (dialogOpen) {
            loadPartyTypes();
            loadMatterTypes();
        }
    }, [dialogOpen]);


    const renderValidationError = (value: any, propertyName: string) => {
        if (isValidate && !value) {
            return <FormHelperText error>{propertyName} is required</FormHelperText>
        }
    }

    return (
            <>
                {typeof children === 'function' ? children(open, close) : children}

                <Dialog open={dialogOpen}
                        onClose={close}
                >
                    <DialogTitle>Create a prospective client</DialogTitle>
                    <DialogContent sx={{minWidth: 400, minHeight: 150}} style={{paddingTop: 5}}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                        autoFocus
                                        required
                                        error={isValidate && !newClient?.name}
                                        id="name"
                                        name="name"
                                        label="Name"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                                />
                                {renderValidationError(newClient?.name, "Name")}
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                        loading={loadingPartyTypes}
                                        value={partyTypes.find(p => p.id === newClient?.partyTypeId) || null}
                                        getOptionLabel={(option) => option.name || ''}
                                        options={partyTypes}
                                        renderInput={(params) => <TextField
                                                error={isValidate && !newClient?.partyTypeId} required {...params}
                                                label="Party"/>}
                                        onChange={(_, value) => setNewClient({
                                            ...newClient,
                                            partyTypeId: value?.id
                                        })}
                                />
                                {renderValidationError(newClient?.partyTypeId, "Party")}
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                        loading={loadingMatterTypes}
                                        options={matterTypes}
                                        value={matterTypes.find(m => m.id === newClient?.matterTypeId) || null}
                                        getOptionLabel={(option) => option.name || ''}
                                        renderInput={(params) => <TextField
                                                error={isValidate && !newClient?.matterTypeId} required {...params}
                                                label="Matter"/>}
                                        onChange={(_, value) => setNewClient({
                                            ...newClient,
                                            matterTypeId: value?.id
                                        })}
                                />
                                {renderValidationError(newClient?.matterTypeId, "Matter")}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color={"error"} onClick={close}>Cancel</Button>
                        <LoadingButton variant={"contained"} onClick={handleCreateClick} loading={processing}>
                            Create Client
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>
    )
}

export default CreateClientDialog;